/**
 * Hardcoded mock fixtures for the /dev/components gallery.
 * All data is static and computed relative to Date.now() so demos look live.
 */

const now = Date.now();
const iso = (offsetMs: number) => new Date(now + offsetMs).toISOString();

export const nextTickPillFixtures = {
  upcoming: {
    lastTickAt: iso(-30_000),
    intervalSec: 120,
    nextTickAt: iso(90_000),
  },
  overdue: {
    lastTickAt: iso(-300_000),
    intervalSec: 120,
    nextTickAt: iso(-60_000),
  },
  missing: null,
};

export const tickPredictionCardFixtures = {
  sample: {
    total: 5,
    byState: { 'Spec: Todo': 2, 'Doing': 3 },
    issues: [
      { identifier: 'CHO-123', title: 'Fix the thing', state: 'Spec: Todo' },
      { identifier: 'CHO-124', title: 'Build the widget', state: 'Doing' },
      { identifier: 'CHO-125', title: 'Refactor the extractor', state: 'Doing' },
    ],
  },
};

export const tickSliderFixtures = {
  ticks: [
    {
      id: 't1',
      tickAt: iso(-4 * 60 * 60_000),
      status: 'ok',
      actual: { processedCount: 12, runs: [] },
      durationMs: 45_000,
    },
    {
      id: 't2',
      tickAt: iso(-3 * 60 * 60_000),
      status: 'ok',
      actual: { processedCount: 8, runs: [] },
      durationMs: 38_000,
    },
    {
      id: 't3',
      tickAt: iso(-2 * 60 * 60_000),
      status: 'warning',
      actual: {
        processedCount: 15,
        runs: [
          {
            ticket_id: 'CHO-100',
            agent: 'kimi',
            outcome: 'failed',
            duration_ms: 12_000,
            error_message: 'Network timeout',
          },
        ],
      },
      durationMs: 120_000,
    },
    {
      id: 't4',
      tickAt: iso(-60 * 60_000),
      status: 'ok',
      actual: { processedCount: 20, runs: [] },
      durationMs: 30_000,
    },
  ],
};

export const statusPillFixtures = {
  running: { relay: { running: true }, queue: { failedTotal: 0 } },
  degraded: { relay: { running: true }, queue: { failedTotal: 3 } },
  down: { relay: { running: false }, queue: { failedTotal: 0 } },
};

export const ownerBadgeStates = [
  'Spec: Todo',
  'Spec: Ready',
  'Doing',
  'PR: Review Requested',
  'PR: Changes Requested',
  'PR: Review Passed',
  'Blocked',
];

export const assigneeBadgeFixtures = {
  kimi: { assignee: { displayName: 'Kimi Agent' }, implementer: 'kimi' },
  hackBot: { assignee: { displayName: 'Hack Bot' }, implementer: 'hack_bot' },
  unknown: { assignee: { name: 'Unknown User' }, implementer: 'other' },
  none: { assignee: null, implementer: 'kimi' },
};

export const boardCardFixtures = {
  issue: {
    identifier: 'CHO-489',
    title: 'Add component gallery route for isolated UI review',
    project: 'CHO',
    url: 'https://linear.app/issue/CHO-489',
    updatedAt: iso(-2 * 60 * 60_000),
    assignee: { displayName: 'Claude Agent' },
    implementer: 'claude',
  },
  state: 'Doing',
  owner: { name: 'Kimi Code Agent', color: '#FFC107' },
};

export const pipelineHealthTileFixtures = {
  ok: {
    status: 'ok',
    stale: false,
    age_minutes: 12,
    timestamp: iso(-12 * 60_000),
    findings: [],
    auto_fixes_applied: [],
  },
  issues: {
    status: 'issues',
    stale: false,
    age_minutes: 45,
    timestamp: iso(-45 * 60_000),
    findings: [
      {
        check: 'stale_checkpoint',
        detail: 'deleted /tmp/linear-agent-checkpoint-CHO-140.json (22h old)',
        severity: 'info',
      },
    ],
    auto_fixes_applied: ['stale_checkpoint:CHO-140'],
  },
  stale: {
    status: 'ok',
    stale: true,
    age_minutes: 200,
    timestamp: iso(-200 * 60_000),
    findings: [],
    auto_fixes_applied: [],
  },
};

export const spinnerFixtures = [16, 24, 40];

export const skeletonFixtures = [
  { className: 'h-4 w-32' },
  { className: 'h-8 w-20 rounded-full' },
  { className: 'h-24 w-full rounded-lg' },
];

export const smokeStatusBadgeFixtures = {
  passed: { status: 'passed' as const, summary: { routeCount: 4, failureCount: 0, elapsedMs: 12_400 } },
  failed: { status: 'failed' as const, summary: { routeCount: 4, failureCount: 2, elapsedMs: 8_200 } },
  deferred: { status: 'deferred' as const, summary: { routeCount: 0, failureCount: 0, elapsedMs: null } },
  not_run: { status: 'not_run' as const, summary: { routeCount: 0, failureCount: 0, elapsedMs: null } },
};

const smokeRoute = (route: string, httpStatus: number, ok: boolean) => ({
  route,
  url: `https://example.com${route}`,
  httpStatus,
  elapsedMs: 1_200,
  assertions: [
    { id: 'status-200', ok },
    { id: 'has-heading', ok },
  ],
  screenshots: { before: null as string | null, after: '/api/pipeline/smoke/CHO-493/screenshot?route=0&kind=after' },
});

export const smokeVerdictPanelFixtures = {
  not_run: null,
  passed: {
    identifier: 'CHO-493',
    status: 'passed' as const,
    exitCode: 0,
    headSha: 'abc1234',
    ranAt: iso(-5 * 60_000),
    elapsedMs: 12_400,
    previewUrl: 'https://cho-493-preview.example.com',
    baselineUrl: 'https://main-preview.example.com',
    account: 'test-account',
    httpStatus: 200,
    schemaVersion: 1,
    routesTested: [
      smokeRoute('/dashboard', 200, true),
      smokeRoute('/settings', 200, true),
    ],
    failures: [],
    warnings: [],
    blockedWrites: [],
    error: null,
  },
  failed: {
    identifier: 'CHO-493',
    status: 'failed' as const,
    exitCode: 1,
    headSha: 'def5678',
    ranAt: iso(-10 * 60_000),
    elapsedMs: 8_200,
    previewUrl: 'https://cho-493-preview.example.com',
    baselineUrl: 'https://main-preview.example.com',
    account: 'test-account',
    httpStatus: 200,
    schemaVersion: 1,
    routesTested: [
      { ...smokeRoute('/dashboard', 200, true), screenshots: { before: null, after: null } },
      { ...smokeRoute('/settings', 500, false), screenshots: { before: null, after: null } },
    ],
    failures: [
      { route: '/settings', id: 'status-200', detail: 'expected 200, got 500' },
      { route: '/settings', id: 'has-heading' },
    ],
    warnings: ['Slow response on /dashboard'],
    blockedWrites: [],
    error: null,
  },
  deferred: {
    identifier: 'CHO-493',
    status: 'deferred' as const,
    exitCode: 2,
    headSha: null,
    ranAt: iso(-15 * 60_000),
    elapsedMs: null,
    previewUrl: null,
    baselineUrl: null,
    account: null,
    httpStatus: null,
    schemaVersion: null,
    routesTested: [],
    failures: [],
    warnings: [],
    blockedWrites: [],
    error: 'Smoke harness could not resolve preview URL',
  },
};

// ── Video Library fixtures ─────────────────────────────────────────────────────
export const videoListFixture: import('../../types/videos').VideoListItem[] = [
  {
    stem: 'DcKAsansjiB',
    filename: 'DcKAsansjiB.mp4',
    title: 'Mock Demo Reel',
    title_source: 'manual' as const,
    size: 12_096_710,
    mtime: iso(-2 * 60 * 60_000),
    duration_seconds: 62.5,
    has_transcript: true,
    has_notes: true,
    has_caption: true,
    tags: ['ecomm', 'ai-dev'],
    job_status: null,
    language: 'es',
    available_languages: ['es', 'en'],
    translation_job_status: null,
    post_url: 'https://www.instagram.com/reel/DcKAsansjiB/',
  },
  {
    stem: 'Db9aX8rhfhU',
    filename: 'Db9aX8rhfhU.mp4',
    title: 'Queued Sample',
    title_source: 'manual' as const,
    size: 2_327_132,
    mtime: iso(-26 * 60 * 60_000),
    duration_seconds: 18.0,
    has_transcript: true,
    has_notes: false,
    has_caption: false,
    tags: [],
    job_status: 'queued',
    language: 'en',
    available_languages: ['en'],
    translation_job_status: null,
    post_url: 'https://www.instagram.com/reel/Db9aX8rhfhU/',
  },
  {
    stem: 'DcHbmmWs1ZL',
    filename: 'DcHbmmWs1ZL.mp4',
    title: 'Ops Clip',
    title_source: 'manual' as const,
    size: 4_101_544,
    mtime: iso(-48 * 60 * 60_000),
    duration_seconds: 35.2,
    has_transcript: false,
    has_notes: false,
    has_caption: false,
    tags: ['ops'],
    job_status: 'error',
    language: null,
    available_languages: [],
    translation_job_status: 'error',
    post_url: 'https://www.instagram.com/reel/DcHbmmWs1ZL/',
  },
];

export const translationJobFixture: import('../../types/videos').TranslationJob = {
  id: 2,
  stem: 'DcKAsansjiB',
  target_lang: 'en',
  status: 'done',
  error: null,
  created_at: iso(-3 * 60 * 60_000),
  started_at: iso(-3 * 60 * 60_000),
  finished_at: iso(-3 * 60 * 60_000 + 45_000),
};

export const videoTitleHeaderFixture = {
  withTitle: { title: 'Mock Demo Reel', filename: 'DcKAsansjiB.mp4' },
  filenameFallback: { title: 'DYSLA3SzfOy', filename: 'DYSLA3SzfOy.mp4' },
};

export const postLinkFixture = {
  withLink: 'https://www.instagram.com/reel/DaBhmyLx9Z7/',
  noLink: null,
};

export const videoDetailFixture: import('../../types/videos').VideoDetail = {
  ...videoListFixture[0],
  transcript: 'This is a mock whisper transcript for the component gallery.\nIt spans multiple lines.',
  notes: 'Pinned for the ecomm pitch deck.',
  job: {
    id: 1,
    stem: 'DcKAsansjiB',
    status: 'done',
    error: null,
    created_at: iso(-3 * 60 * 60_000),
    started_at: iso(-3 * 60 * 60_000),
    finished_at: iso(-3 * 60 * 60_000 + 45_000),
  },
  language_confidence: 0.97,
  requested_language: 'es',
  lang_fallback: false,
  translation_job: translationJobFixture,
  caption: 'Mock caption for the component gallery.',
  caption_raw: 'Mock caption for the component gallery.',
  caption_source: 'manual',
  caption_meta: null,
};

export const tuningFixture: import('../../types/videos').TuningData = {
  labelled_count: 7,
  unlabelled_count: 3,
  total: 10,
  min_labeled_samples: 25,
  suggestions_enabled: false,
  distribution: [
    { tag: 'ecomm', count: 4 },
    { tag: 'ai-dev', count: 2 },
    { tag: 'ops', count: 1 },
  ],
};

export const jobBadgeFixtures = [
  { status: 'queued' as const },
  { status: 'running' as const },
  { status: 'done' as const },
  { status: 'error' as const, error: 'out of memory' },
  { status: null },
];

export const tagSuggestionFixtures = {
  populated: ['ecomm', 'ai-dev', 'hooks', 'ops', 'tutorial', 'shadcn', 'agent-loop', 'ui-policy'],
  empty: [] as string[],
};

const suggestionBase = {
  stem: 'demo-stem',
  status: 'pending' as const,
  model: 'claude-sonnet-5',
  rubric_version: 'demo',
  suggested_at: '2026-09-18T09:00:00Z',
  decided_at: null,
};

/** Pending LLM proposals covering all four tag states for the gallery demo. */
export const tagPendingSuggestionFixtures = [
  { ...suggestionBase, id: 1, tag: 'tutorial', action: 'add' as const, rationale: 'The caption walks through a step-by-step setup.', is_new_tag: 0 },
  { ...suggestionBase, id: 2, tag: 'ugc-script', action: 'add' as const, rationale: 'Notes describe a repeatable script framework.', is_new_tag: 1 },
  { ...suggestionBase, id: 3, tag: 'ecomm', action: 'remove' as const, rationale: 'No shop or product mention anywhere in the evidence.', is_new_tag: 0 },
];

// ── Flights (CHO-559) ─────────────────────────────────────────────────────────

const flightObsBase = {
  watch_name: 'zrh-bkk-autumn',
  provider: 'google-flights',
  origin: 'ZRH',
  destination: 'BKK',
  trip_type: 'return',
  cabin: 'economy',
  currency: 'CHF',
  stopover_point: null as string | null,
  stopover_leg: null as string | null,
  stopover_nights: null as number | null,
  fare_family: null as string | null,
  hotel_offer_note: null as string | null,
  raw_json: null as string | null,
};

export const flightCheapestFixtures = {
  rows: [
    { ...flightObsBase, id: 1, observed_at: iso(-3600_000), outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 535 },
    { ...flightObsBase, id: 2, observed_at: iso(-3600_000), outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'stopover' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 554, stopover_point: 'AUH', stopover_leg: 'inbound', stopover_nights: 2 },
    { ...flightObsBase, id: 3, observed_at: iso(-3600_000), outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 654, provider: 'kayak', fare_family: 'Saver' },
    { ...flightObsBase, id: 4, observed_at: iso(-3600_000), outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'stopover' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 727, stopover_point: 'DXB', stopover_leg: 'inbound', stopover_nights: 2, hotel_offer_note: 'Dubai Connect eligible per EK chat 08/19' },
    { ...flightObsBase, id: 5, observed_at: iso(-3600_000), outbound_date: '2026-09-21', inbound_date: null, itinerary_shape: 'oneway' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 559 },
    { ...flightObsBase, id: 6, observed_at: iso(-3600_000), outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'LX', price: 860 },
    { ...flightObsBase, id: 7, observed_at: iso(-3600_000), origin: 'BKK', destination: 'ZRH', outbound_date: '2026-11-01', inbound_date: null, trip_type: 'oneway', itinerary_shape: 'oneway' as const, leg_direction: 'inbound' as const, carrier: 'EY', price: 347 },
    { ...flightObsBase, id: 8, observed_at: iso(-3600_000), origin: 'ZRH', destination: 'DMK', outbound_date: '2026-09-22', inbound_date: null, trip_type: 'oneway', itinerary_shape: 'oneway' as const, leg_direction: 'outbound' as const, carrier: '??', price: 1432 },
    { ...flightObsBase, id: 9, observed_at: iso(-3600_000), outbound_date: '2026-09-24', inbound_date: null, itinerary_shape: 'stopover' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 499, stopover_point: 'DXB', stopover_leg: 'outbound', stopover_nights: 2 },
  ],
  groups: [
    { group_key: 'ONEWAY:INBOUND' as const, itinerary_shape: 'oneway' as const, count: 1, rows: [
      { ...flightObsBase, id: 7, observed_at: iso(-3600_000), origin: 'BKK', destination: 'ZRH', outbound_date: '2026-11-01', inbound_date: null, trip_type: 'oneway', itinerary_shape: 'oneway' as const, leg_direction: 'inbound' as const, carrier: 'EY', price: 347 },
    ]},
    { group_key: 'ONEWAY:OUTBOUND' as const, itinerary_shape: 'oneway' as const, count: 3, rows: [
      { ...flightObsBase, id: 5, observed_at: iso(-3600_000), outbound_date: '2026-09-21', inbound_date: null, itinerary_shape: 'oneway' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 559 },
      { ...flightObsBase, id: 8, observed_at: iso(-3600_000), origin: 'ZRH', destination: 'DMK', outbound_date: '2026-09-22', inbound_date: null, trip_type: 'oneway', itinerary_shape: 'oneway' as const, leg_direction: 'outbound' as const, carrier: '??', price: 1432 },
      { ...flightObsBase, id: 9, observed_at: iso(-3600_000), outbound_date: '2026-09-24', inbound_date: null, itinerary_shape: 'stopover' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 499, stopover_point: 'DXB', stopover_leg: 'outbound', stopover_nights: 2 },
    ]},
    { group_key: 'ROUNDTRIP' as const, itinerary_shape: 'return' as const, count: 5, rows: [
      { ...flightObsBase, id: 1, observed_at: iso(-3600_000), outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 535 },
      { ...flightObsBase, id: 2, observed_at: iso(-3600_000), outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'stopover' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 554, stopover_point: 'AUH', stopover_leg: 'inbound', stopover_nights: 2 },
      { ...flightObsBase, id: 3, observed_at: iso(-3600_000), outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 654, provider: 'kayak', fare_family: 'Saver' },
      { ...flightObsBase, id: 4, observed_at: iso(-3600_000), outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'stopover' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 727, stopover_point: 'DXB', stopover_leg: 'inbound', stopover_nights: 2, hotel_offer_note: 'Dubai Connect eligible per EK chat 08/19' },
    ]},
  ] as import('../../types/flights').CheapestShapeGroup[],
  empty: [] as typeof flightObsBase[] & never[],
};

const freshnessNow = Date.now();
const freshObservedAt = iso(-3600_000);
const stale2dObservedAt = iso(-2 * 86_400_000);
const stale8dObservedAt = iso(-8 * 86_400_000);

export const flightFreshnessFixtures = {
  now: freshnessNow,
  mixed: {
    rows: [
      { ...flightObsBase, id: 201, observed_at: freshObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 535 },
      { ...flightObsBase, id: 202, observed_at: freshObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'stopover' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 554, stopover_point: 'AUH', stopover_leg: 'inbound', stopover_nights: 2 },
      { ...flightObsBase, id: 203, observed_at: stale2dObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 654, provider: 'kayak', fare_family: 'Saver' },
      { ...flightObsBase, id: 204, observed_at: stale8dObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'stopover' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 727, stopover_point: 'DXB', stopover_leg: 'inbound', stopover_nights: 2, hotel_offer_note: 'Dubai Connect eligible per EK chat 08/19' },
      { ...flightObsBase, id: 205, observed_at: freshObservedAt, outbound_date: '2026-09-21', inbound_date: null, itinerary_shape: 'oneway' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 559 },
    ],
    groups: [
      { group_key: 'ONEWAY:OUTBOUND' as const, itinerary_shape: 'oneway' as const, count: 1, rows: [
        { ...flightObsBase, id: 205, observed_at: freshObservedAt, outbound_date: '2026-09-21', inbound_date: null, itinerary_shape: 'oneway' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 559 },
      ]},
      { group_key: 'ROUNDTRIP' as const, itinerary_shape: 'return' as const, count: 4, rows: [
        { ...flightObsBase, id: 201, observed_at: freshObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 535 },
        { ...flightObsBase, id: 202, observed_at: freshObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'stopover' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 554, stopover_point: 'AUH', stopover_leg: 'inbound', stopover_nights: 2 },
        { ...flightObsBase, id: 203, observed_at: stale2dObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 654, provider: 'kayak', fare_family: 'Saver' },
        { ...flightObsBase, id: 204, observed_at: stale8dObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'stopover' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 727, stopover_point: 'DXB', stopover_leg: 'inbound', stopover_nights: 2, hotel_offer_note: 'Dubai Connect eligible per EK chat 08/19' },
      ]},
    ] as import('../../types/flights').CheapestShapeGroup[],
    freshness: {
      lastRunAt: freshObservedAt,
      cutoffAt: freshObservedAt,
      refreshed: 3,
      total: 5,
    },
  },
  allFresh: {
    rows: [
      { ...flightObsBase, id: 211, observed_at: freshObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 535 },
      { ...flightObsBase, id: 212, observed_at: freshObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 654 },
    ],
    groups: [
      { group_key: 'ROUNDTRIP' as const, itinerary_shape: 'return' as const, count: 2, rows: [
        { ...flightObsBase, id: 211, observed_at: freshObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EY', price: 535 },
        { ...flightObsBase, id: 212, observed_at: freshObservedAt, outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, leg_direction: 'outbound' as const, carrier: 'EK', price: 654 },
      ]},
    ] as import('../../types/flights').CheapestShapeGroup[],
    freshness: {
      lastRunAt: freshObservedAt,
      cutoffAt: freshObservedAt,
      refreshed: 2,
      total: 2,
    },
  },
};

export const flightMoversFixtures = {
  movers: [
    { ...flightObsBase, id: 11, observed_at: iso(-3600_000), outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, carrier: 'EK', price: 654, provider: 'kayak', previous_price: 719, previous_observed_at: iso(-90_000_000), delta: -65 },
    { ...flightObsBase, id: 12, observed_at: iso(-3600_000), outbound_date: '2026-09-26', inbound_date: '2026-11-02', itinerary_shape: 'return' as const, carrier: 'EY', price: 612, previous_price: 547, previous_observed_at: iso(-90_000_000), delta: 65 },
    { ...flightObsBase, id: 13, observed_at: iso(-3600_000), outbound_date: '2026-09-23', inbound_date: '2026-11-02', itinerary_shape: 'stopover' as const, carrier: 'EK', price: 727, stopover_point: 'DXB', stopover_nights: 3, previous_price: 764, previous_observed_at: iso(-90_000_000), delta: -37 },
    { ...flightObsBase, id: 14, observed_at: iso(-3600_000), origin: 'BKK', destination: 'ZRH', outbound_date: '2026-11-01', inbound_date: null, trip_type: 'oneway', itinerary_shape: 'oneway' as const, carrier: 'EY', price: 347, previous_price: 320, previous_observed_at: iso(-90_000_000), delta: 27 },
    { ...flightObsBase, id: 15, observed_at: iso(-3600_000), origin: 'ZRH', destination: 'DMK', outbound_date: '2026-09-22', inbound_date: null, trip_type: 'oneway', itinerary_shape: 'oneway' as const, carrier: '??', price: 1432, previous_price: 1450, previous_observed_at: iso(-90_000_000), delta: -18 },
    { ...flightObsBase, id: 16, observed_at: iso(-3600_000), outbound_date: '2026-09-24', inbound_date: null, itinerary_shape: 'stopover' as const, carrier: 'EY', price: 499, stopover_point: 'DXB', stopover_leg: 'outbound', stopover_nights: 2, previous_price: 540, previous_observed_at: iso(-90_000_000), delta: -41 },
  ],
  threshold: 25,
};

export const flightSeriesFixtures = {
  series: [
    { label: 'EY return', points: Array.from({ length: 14 }, (_, i) => ({ observed_at: iso((i - 14) * 86_400_000), price: 535 + Math.round(40 * Math.sin(i / 2)) })) },
    { label: 'EK return', points: Array.from({ length: 14 }, (_, i) => ({ observed_at: iso((i - 14) * 86_400_000), price: 669 + Math.round(30 * Math.cos(i / 3)) })) },
    { label: 'EK stopover via DXB', points: Array.from({ length: 8 }, (_, i) => ({ observed_at: iso((i - 8) * 86_400_000), price: 741 + i * 4 })) },
  ],
  empty: [] as { label: string; points: { observed_at: string; price: number }[] }[],
};

export const flightPairFixtures: import('../../components/flights/flightPair').FlightPair[] = [
  {
    origin: 'ZRH', destination: 'BKK', outbound_date: '2026-09-23', inbound_date: '2026-11-02',
    current_min: 535, baseline_min: 608, delta: -73, pct: -12.0, direction: 'down', distinct_prices: 5, currency: 'CHF',
  },
  {
    origin: 'BKK', destination: 'ZRH', outbound_date: '2026-11-01', inbound_date: null,
    current_min: 347, baseline_min: 337, delta: 10, pct: 3.0, direction: 'up', distinct_prices: 3, currency: 'CHF',
  },
  {
    origin: 'ZRH', destination: 'DMK', outbound_date: '2026-09-22', inbound_date: null,
    current_min: 1432, baseline_min: 1432, delta: 0, pct: 0, direction: 'flat', distinct_prices: 1, currency: 'CHF',
  },
  {
    origin: 'BKK', destination: 'ZRH', outbound_date: '2026-10-30', inbound_date: null,
    current_min: 412, baseline_min: null, delta: 0, pct: null, direction: 'flat', distinct_prices: 1, currency: 'CHF',
  },
];

export const flightRunFixtures = {
  healthy: [
    { id: 101, started_at: iso(-3600_000), finished_at: iso(-3000_000), watch_name: 'zrh-bkk-autumn', provider: 'google-flights', query_shape: 'oneway', rows_written: 108, status: 'ok' as const, error: null },
    { id: 102, started_at: iso(-3000_000), finished_at: iso(-2400_000), watch_name: 'zrh-bkk-autumn', provider: 'kayak', query_shape: 'return', rows_written: 15, status: 'ok' as const, error: null },
  ],
  failing: [
    { id: 103, started_at: iso(-3600_000), finished_at: iso(-3300_000), watch_name: 'zrh-bkk-autumn', provider: 'kayak', query_shape: 'return', rows_written: 0, status: 'failed' as const, error: '15/15 queries failed' },
    { id: 104, started_at: iso(-90_000_000), finished_at: iso(-89_000_000), watch_name: 'zrh-bkk-autumn', provider: 'google-flights', query_shape: 'return', rows_written: 42, status: 'ok' as const, error: null },
  ],
};

import type { FlightFilterOptions, FlightFilterState } from '../../components/flights/flightFilters';

export const flightFilterBarFixtures: Record<
  string,
  { options: FlightFilterOptions; value: FlightFilterState; matched: number; total: number }
> = {
  full: {
    options: {
      home_origin: 'ZRH',
      outbound: { min: '2026-09-18', max: '2026-09-30' },
      inbound: { min: '2026-10-27', max: '2026-11-04' },
      stopover_nights: { min: 2, max: 3, values: [2, 3] },
      stopover_legs: ['inbound'],
      carriers: ['EK', 'EY', 'LX', 'WY', '??'],
    },
    value: {
      outbound: ['2026-09-20', '2026-09-25'],
      inbound: ['2026-10-29', '2026-11-02'],
      nights: [2, 3],
      leg: null,
      carriers: [],
      stopover: 'any',
    },
    matched: 4,
    total: 8,
  },
  singleNight: {
    options: {
      home_origin: 'ZRH',
      outbound: { min: '2026-09-18', max: '2026-09-30' },
      inbound: { min: '2026-10-27', max: '2026-11-04' },
      stopover_nights: { min: 2, max: 2, values: [2] },
      stopover_legs: ['inbound'],
      carriers: ['EK', 'EY'],
    },
    value: {
      outbound: ['2026-09-18', '2026-09-30'],
      inbound: ['2026-10-27', '2026-11-04'],
      nights: [2, 2],
      leg: null,
      carriers: [],
      stopover: 'any',
    },
    matched: 8,
    total: 8,
  },
  noInbound: {
    options: {
      home_origin: 'ZRH',
      outbound: { min: '2026-09-18', max: '2026-09-30' },
      inbound: null,
      stopover_nights: { min: 2, max: 3, values: [2, 3] },
      stopover_legs: ['inbound'],
      carriers: ['EK', 'EY'],
    },
    value: {
      outbound: ['2026-09-18', '2026-09-30'],
      inbound: null,
      nights: [2, 3],
      leg: null,
      carriers: [],
      stopover: 'any',
    },
    matched: 5,
    total: 5,
  },
  missingOutboundLeg: {
    options: {
      home_origin: 'ZRH',
      outbound: { min: '2026-09-18', max: '2026-09-30' },
      inbound: { min: '2026-10-27', max: '2026-11-04' },
      stopover_nights: { min: 2, max: 3, values: [2, 3] },
      stopover_legs: ['inbound'],
      carriers: ['EK', 'EY'],
    },
    value: {
      outbound: ['2026-09-18', '2026-09-30'],
      inbound: ['2026-10-27', '2026-11-04'],
      nights: [2, 3],
      leg: null,
      carriers: [],
      stopover: 'any',
    },
    matched: 8,
    total: 8,
  },
  multiCarrier: {
    options: {
      home_origin: 'ZRH',
      outbound: { min: '2026-09-18', max: '2026-09-30' },
      inbound: { min: '2026-10-27', max: '2026-11-04' },
      stopover_nights: { min: 2, max: 3, values: [2, 3] },
      stopover_legs: ['inbound'],
      carriers: ['EK', 'EY', 'LX', 'WY', '??'],
    },
    value: {
      outbound: ['2026-09-18', '2026-09-30'],
      inbound: ['2026-10-27', '2026-11-04'],
      nights: [2, 3],
      leg: null,
      carriers: ['EY', '??'],
      stopover: 'any',
    },
    matched: 3,
    total: 8,
  },
  directOnly: {
    options: {
      home_origin: 'ZRH',
      outbound: { min: '2026-09-18', max: '2026-09-30' },
      inbound: { min: '2026-10-27', max: '2026-11-04' },
      stopover_nights: { min: 2, max: 3, values: [2, 3] },
      stopover_legs: ['inbound'],
      carriers: ['EK', 'EY', 'LX', 'WY', '??'],
    },
    value: {
      outbound: ['2026-09-18', '2026-09-30'],
      inbound: ['2026-10-27', '2026-11-04'],
      nights: [1, 7],
      leg: 'inbound',
      carriers: [],
      stopover: 'direct',
    },
    matched: 6,
    total: 8,
  },
  noStopoversObserved: {
    options: {
      home_origin: 'ZRH',
      outbound: { min: '2026-09-18', max: '2026-09-30' },
      inbound: null,
      stopover_nights: null,
      stopover_legs: [],
      carriers: ['EK', 'EY'],
    },
    value: {
      outbound: ['2026-09-18', '2026-09-30'],
      inbound: null,
      nights: [1, 14],
      leg: null,
      carriers: [],
      stopover: 'any',
    },
    matched: 5,
    total: 5,
  },
};

export const stopoverPresenceFixtures = {
  any: { value: 'any' as const },
  directOnly: { value: 'direct' as const },
  withStopover: { value: 'stopover' as const },
};

export const carrierFilterChipsFixtures = {
  noneSelected: {
    carriers: ['EK', 'EY', 'LX', 'WY'],
    value: [] as string[],
  },
  twoSelected: {
    carriers: ['EK', 'EY', 'LX', 'WY'],
    value: ['EK', 'WY'],
  },
  withUnknown: {
    carriers: ['EK', 'EY', '??'],
    value: ['??'],
  },
  singleCarrier: {
    carriers: ['EY'],
    value: [] as string[],
  },
};

// ── Prediction attrition funnel (CHO-581) ──────────────────────────────────────

export const funnelStageProvenanceFixtures = {
  threeModels: {
    kind: 'models' as const,
    source: 'predictions.structure_model',
    models: [
      { model: 'kimi-code/k3-low', count: 2218, pct: 98.1 },
      { model: 'sonnet', count: 42, pct: 1.9 },
      { model: 'opus', count: 1, pct: 0.0 },
    ],
  },
  overflow: {
    kind: 'models' as const,
    source: 'predictions.structure_model',
    models: [
      { model: 'kimi-code/k3-low', count: 1800, pct: 80.0 },
      { model: 'sonnet', count: 300, pct: 13.3 },
      { model: 'opus', count: 100, pct: 4.4 },
      { model: 'gpt-4o', count: 50, pct: 2.2 },
      { model: 'ollama/llama3', count: 10, pct: 0.4 },
    ],
  },
  deterministic: {
    kind: 'deterministic' as const,
    note: 'no model — computed from stored values',
  },
  notRecorded: {
    kind: 'not_recorded' as const,
    note: 'provenance is not stored for this stage',
  },
};

export const funnelStageLivenessFixtures = {
  derived: {
    kind: 'derived' as const,
    status: null,
  },
  unscheduled: {
    kind: 'runnable' as const,
    status: 'unscheduled' as const,
    scheduled: false,
    age_minutes: null,
    expected_interval_min: 120,
  },
  ok: {
    kind: 'runnable' as const,
    status: 'ok' as const,
    scheduled: true,
    age_minutes: 12,
    expected_interval_min: 120,
  },
  warning: {
    kind: 'runnable' as const,
    status: 'warning' as const,
    scheduled: true,
    age_minutes: 210,
    expected_interval_min: 120,
  },
  down: {
    kind: 'runnable' as const,
    status: 'down' as const,
    scheduled: true,
    age_minutes: 310,
    expected_interval_min: 120,
  },
  running: {
    // Manual run in progress on an unscheduled stage — exercises the CHO-728
    // precedence rule where a live run overrides 'not scheduled'.
    kind: 'runnable' as const,
    status: 'running' as const,
    scheduled: false,
    age_minutes: 45,
    expected_interval_min: 120,
    run_started_at: iso(-4 * 60_000),
  },
};

export const predictionFunnelFixture = {
  as_of: iso(-5 * 60_000),
  total: 12215,
  stages: [
    {
      key: 'extracted',
      label: 'Predictions extracted',
      count: 12215,
      drop: 0,
      drop_label: null,
      category: null,
      provenance: funnelStageProvenanceFixtures.notRecorded,
    },
    {
      key: 'asset_identified',
      label: 'Asset identified',
      count: 6265,
      drop: 5950,
      drop_label: 'no asset',
      category: 'by_design' as const,
      provenance: funnelStageProvenanceFixtures.notRecorded,
    },
    {
      key: 'has_figure',
      label: 'Carries a price-like figure',
      count: 2404,
      drop: 3861,
      drop_label: 'no number',
      category: 'by_design' as const,
      provenance: funnelStageProvenanceFixtures.deterministic,
    },
    {
      key: 'structure_extracted',
      label: 'Structure extracted',
      count: 2260,
      drop: 144,
      drop_label: 'extraction unfinished',
      category: 'fixable' as const,
      provenance: funnelStageProvenanceFixtures.threeModels,
    },
    {
      key: 'measurable',
      label: 'Measurable claim',
      count: 1079,
      drop: 1181,
      drop_label: 'no threshold',
      category: 'by_design' as const,
      provenance: funnelStageProvenanceFixtures.notRecorded,
    },
    {
      key: 'condition_resolvable',
      label: 'Trigger is checkable',
      count: 1079,
      drop: 0,
      drop_label: null,
      category: null,
      provenance: funnelStageProvenanceFixtures.notRecorded,
    },
    {
      key: 'has_resolve_date',
      label: 'Has a resolve date',
      count: 649,
      drop: 430,
      drop_label: 'no horizon',
      category: 'by_design' as const,
      provenance: funnelStageProvenanceFixtures.deterministic,
    },
    {
      key: 'due',
      label: 'Due for judgement',
      count: 583,
      drop: 66,
      drop_label: 'resolves in future',
      category: 'waiting' as const,
      provenance: funnelStageProvenanceFixtures.deterministic,
    },
    {
      key: 'graded',
      label: 'Graded',
      count: 508,
      drop: 75,
      drop_label: 'unresolvable',
      category: 'fixable' as const,
      provenance: funnelStageProvenanceFixtures.notRecorded,
    },
  ],
};

// ── Scoreboard coverage cell (CHO-649) ─────────────────────────────────────────

export const scoreboardCoverageFixtures = [
  { name: 'Typical (Ben Felix)', inWindow: 2, extracted: 69 },
  { name: 'Nothing scoreable yet', inWindow: 0, extracted: 41 },
  { name: 'No data', inWindow: 0, extracted: 0 },
  { name: 'Fully resolved', inWindow: 18, extracted: 18 },
  { name: 'Skew (cache race)', inWindow: 5, extracted: 4 },
];

export const funnelDrilldownRowsFixture: import('../../components/intel/FunnelDrilldownPanel').Prediction[] = [
  {
    id: 101,
    statement: 'Bitcoin will hit $100,000 by the end of 2025 as institutional adoption accelerates and ETF inflows sustain demand.',
    influencer_name: 'Michael Saylor',
    prediction_date: '2025-01-15',
    direction: 'bullish',
    topic: 'crypto',
    asset: 'BTC',
    confidence_pct: 90,
    timeframe_raw: 'by end of 2025',
    brier_score: 0.25,
    outcome: 'incorrect',
  },
  {
    id: 102,
    statement: 'Gold consolidates above $2,400 through Q3 before a hawkish Fed reprices the metal lower into year-end.',
    influencer_name: 'Peter Schiff',
    prediction_date: '2025-06-20',
    direction: 'bearish',
    topic: 'gold',
    asset: 'XAU',
    confidence_pct: 75,
    timeframe_raw: 'Q3–Q4 2025',
    brier_score: null,
    outcome: 'unknown',
  },
  {
    id: 103,
    statement: 'The S&P 500 ends 2025 flat as earnings growth is offset by multiple compression from elevated real rates.',
    influencer_name: 'Mohamed El-Erian',
    prediction_date: '2025-02-10',
    direction: 'neutral',
    topic: 'equities',
    asset: 'SPX',
    confidence_pct: 60,
    timeframe_raw: 'end of 2025',
    brier_score: 0.0,
    outcome: 'correct',
  },
];

// ── PC Monitor (CHO-706) ────────────────────────────────────────────────────
// Row shape mirrors the CHO-705 dataset contract (see client/src/types/pcmonitor.ts).

const pcMonitorNow = Date.now();
const pcMonitorIso = (offsetMs: number) => new Date(pcMonitorNow + offsetMs).toISOString();

const pcMonitorBase = {
  id: 'tutti-demo-1',
  url: 'https://www.tutti.ch/de/ad/demo-1',
  title: 'Gaming PC Ryzen 9800X3D, 64GB DDR5, RTX 5090',
  provider: 'tutti',
  location: 'Zürich',
  asking_price: 1200,
  currency: 'CHF',
  price_resolved: true,
  posted_date: '2026-09-10',
  estimated_value: 3100,
  verdict: 'qualifying',
  rejection_reasons: [],
  high_priority: true,
  card_only: false,
  needs: ['gpu'],
  flags: ['underpriced'],
  thumbnail_url:
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="112" height="80"><rect width="112" height="80" fill="#1f2937"/><rect x="34" y="14" width="44" height="34" rx="2" fill="#374151"/><rect x="48" y="52" width="16" height="6" fill="#4b5563"/><rect x="40" y="58" width="32" height="4" rx="2" fill="#4b5563"/><circle cx="56" cy="31" r="6" fill="#6b7280"/></svg>',
    ),
  first_seen: pcMonitorIso(-3 * 86_400_000),
  last_seen: pcMonitorIso(-3 * 3_600_000),
  price_history: [
    { price: 1500, ts: pcMonitorIso(-3 * 86_400_000) },
    { price: 1200, ts: pcMonitorIso(-3 * 3_600_000) },
  ],
};

export const pcMonitorListingFixtures = {
  /** Normal qualifying row: photo, drop, positive gap, high priority. */
  normal: pcMonitorBase,
  /** Ricardo bot-blocks detail pages — no image must not collapse anything. */
  missingPhoto: {
    ...pcMonitorBase,
    id: 'ricardo-demo-2',
    provider: 'ricardo',
    title: 'Workstation Xeon, 128GB DDR5 ECC, RTX A4000',
    asking_price: 450,
    estimated_value: 1400,
    high_priority: false,
    thumbnail_url: null,
    price_history: [{ price: 450, ts: pcMonitorIso(-86_400_000) }],
  },
  /** CHO-698: unresolved price is a badge, never a number, no gap. */
  unresolvedPrice: {
    ...pcMonitorBase,
    id: 'tutti-demo-3',
    title: 'Gaming PC mit neuer RTX 5090',
    asking_price: null,
    price_resolved: false,
    estimated_value: null,
    verdict: 'rejected',
    rejection_reasons: ['price_unresolved'],
    high_priority: false,
    flags: [],
    price_history: [],
  },
  /**
   * Resolved price, no estimate: the evaluator recognised no major component
   * (CHO-705 honest null) — a valuation gap, not a price problem. Must not
   * render the CHO-698 "Price unresolved" badge.
   */
  resolvedNoEstimate: {
    ...pcMonitorBase,
    id: 'tutti-demo-6',
    title: 'Gaming PC i9-14900K, RTX 5080, 64GB DDR5',
    asking_price: 4930,
    estimated_value: null,
    high_priority: false,
    flags: [],
    price_history: [{ price: 4930, ts: pcMonitorIso(-2 * 86_400_000) }],
  },
  /** Hard-rejected row carries the exact evaluator failure strings. */
  rejected: {
    ...pcMonitorBase,
    id: 'tutti-demo-4',
    title: 'PC i5-8400, 16GB DDR4, GTX 1050',
    asking_price: 300,
    estimated_value: 350,
    verdict: 'rejected',
    rejection_reasons: ['no_ddr5', 'gpu_below_floor'],
    high_priority: false,
    flags: [],
  },
  /** Needs-confirmation rows are candidates, not rejects. */
  needsConfirmation: {
    ...pcMonitorBase,
    id: 'ricardo-demo-5',
    provider: 'ricardo',
    title: 'Ryzen 7950X Build, 64GB (DDR5 wahrscheinlich)',
    asking_price: 900,
    estimated_value: 1700,
    verdict: 'needs_confirmation',
    high_priority: false,
    thumbnail_url: null,
  },
};

// ── Flight tracker watches (CHO-769) — document shape, mirrors GET /api/flights/watches

export const watchListFixtures: import('../../types/flights').TrackerWatch[] = [
  {
    name: 'zrh-bkk-autumn',
    origin: 'ZRH',
    destinations: ['BKK', 'SIN'],
    outbound: { from: '2026-12-01', to: '2026-12-20' },
    inbound: { from: '2027-01-05', to: '2027-01-25' },
    trip_types: ['return', 'oneway'],
    carriers: ['EY', 'EK', 'TG'],
    cabin: 'economy',
    passengers: 2,
    currency: 'CHF',
    stopovers: [
      { carrier: 'EY', point: 'AUH', leg: 'inbound', nights: { min: 1, max: 3 }, also_try: 'outbound' },
    ],
    enabled: true,
    created_at: '2026-09-01T03:51:00+00:00',
    updated_at: '2026-09-18T03:51:00+00:00',
  },
  {
    name: 'winter-hkt',
    origin: 'ZRH',
    destinations: ['HKT'],
    outbound: { from: '2027-02-01', to: '2027-02-14' },
    inbound: { from: '2027-03-01', to: '2027-03-14' },
    trip_types: ['return'],
    carriers: ['QR'],
    cabin: 'economy',
    passengers: 1,
    currency: 'CHF',
    stopovers: [],
    enabled: false,
    created_at: '2026-09-10T03:51:00+00:00',
    updated_at: '2026-09-15T03:51:00+00:00',
  },
];
